/**
 * Shared nutrition chart primitives consumed by DietRecallTab and MealPlanTab.
 *
 * - All pies render through shadcn `<ChartContainer>` (see src/components/ui/chart.tsx)
 * - Colors come from theme tokens (`--nutrition-*` in src/index.css, mirrored
 *   in src/design-system/tokens/colors.ts under `nutrition` / `nutritionTokens`)
 * - Segment shape uses a typed `key` matching NUTRITION_CHART_CONFIG entries so
 *   the chart, hover/tooltip, and legend all share one source of truth.
 *
 * Tokens, types, and the ChartConfig live in `./nutrition-chart-config` so this
 * file only exports React components (Fast Refresh friendly).
 */
import * as React from "react";
import { Pie, PieChart } from "recharts";

import { ChartContainer } from "@/components/ui/chart";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  NUTRITION_CHART_CONFIG,
  NUTRITION_COLORS,
  type NutrientPieSegment,
} from "./nutrition-chart-config";

// =============================================================================
// NutrientPieChart — large macro/energy donut with HoverCard breakdown table
// =============================================================================

interface NutrientPieChartProps {
  title: string;
  value: number;
  /** Currently unused but kept for callsite compatibility / future axis labels. */
  total?: number;
  segments: NutrientPieSegment[];
  showKcal?: boolean;
}

export const NutrientPieChart: React.FC<NutrientPieChartProps> = ({
  title,
  value,
  segments,
  showKcal = true,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const totalValue = segments.reduce((sum, seg) => sum + seg.value, 0);
  const activeSegments = segments.filter((seg) => seg.value > 0);
  const chartData =
    totalValue > 0
      ? activeSegments.map((seg) => ({
          name: seg.key,
          value: seg.value,
          fill: `var(--color-${seg.key})`,
        }))
      : [{ name: "empty", value: 1, fill: `var(--color-empty)` }];

  return (
    <HoverCard open={isHovered} openDelay={0} closeDelay={0}>
      <HoverCardTrigger asChild>
        <div
          className="flex flex-col items-center gap-2 cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative w-28 h-28">
            <ChartContainer
              config={NUTRITION_CHART_CONFIG}
              className="aspect-square h-28 w-28"
            >
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={36}
                  outerRadius={56}
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  isAnimationActive={false}
                />
              </PieChart>
            </ChartContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="z-h2">{Math.round(value)}</span>
              {showKcal && (
                <span className="z-caption text-muted-foreground">kcal</span>
              )}
            </div>
          </div>
          <span className="z-h3 text-foreground">{title}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent side="top" className="w-80 p-0 overflow-hidden">
        <div className="z-body-md text-foreground px-3 py-2 border-b border-border">
          {title} <span className="text-muted-foreground">(kcal)</span>
        </div>
        <Table>
          <TableBody>
            {segments.map((seg) => {
              const pct = totalValue > 0 ? (seg.value / totalValue) * 100 : 0;
              const cfg = NUTRITION_CHART_CONFIG[seg.key];
              const swatchColor = NUTRITION_COLORS[seg.key];
              return (
                <TableRow key={seg.key} className="hover:bg-transparent">
                  <TableCell className="px-3" style={{ color: swatchColor }}>
                    {cfg.label}
                  </TableCell>
                  <TableCell
                    className="px-3 text-right tabular-nums"
                    style={{ color: swatchColor }}
                  >
                    {seg.value.toFixed(2)}
                  </TableCell>
                  <TableCell
                    className="px-3 text-right tabular-nums"
                    style={{ color: swatchColor }}
                  >
                    {pct.toFixed(2)}%
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableCell className="px-3 z-body-md text-foreground">
                Total
              </TableCell>
              <TableCell className="px-3 text-right z-body-md text-foreground tabular-nums">
                {totalValue.toFixed(2)}
              </TableCell>
              <TableCell className="px-3 text-right z-body-md text-foreground tabular-nums">
                100%
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </HoverCardContent>
    </HoverCard>
  );
};

// =============================================================================
// FoodNutrientPieChart — compact macro donut (used in Add Food dialogs)
// =============================================================================

interface FoodNutrientPieChartProps {
  energy: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

export const FoodNutrientPieChart: React.FC<FoodNutrientPieChartProps> = ({
  energy,
  protein,
  carbs,
  fat,
  fiber,
}) => {
  // Calculate energy from macros (matching Flutter calculation)
  const proteinKcal = protein * 4;
  const carbsKcal = carbs * 4;
  const fatKcal = fat * 9;
  const fiberKcal = fiber * 2;
  const totalEnergy =
    energy > 0 ? energy : proteinKcal + carbsKcal + fatKcal + fiberKcal;

  // Slice order matches Flutter pie chart (carbs → protein → fiber → fats)
  const macroSegments: NutrientPieSegment[] = [
    { key: "carbs", value: carbsKcal },
    { key: "protein", value: proteinKcal },
    { key: "fiber", value: fiberKcal },
    { key: "fats", value: fatKcal },
  ];

  const chartData =
    totalEnergy > 0
      ? macroSegments
          .filter((seg) => seg.value > 0)
          .map((seg) => ({
            name: seg.key,
            value: seg.value,
            fill: `var(--color-${seg.key})`,
          }))
      : [{ name: "empty", value: 1, fill: `var(--color-empty)` }];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-20 h-20">
        <ChartContainer
          config={NUTRITION_CHART_CONFIG}
          className="aspect-square h-20 w-20"
        >
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              innerRadius={26}
              outerRadius={40}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            />
          </PieChart>
        </ChartContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="z-h3 text-foreground">
            {Math.round(totalEnergy)}
          </span>
          <span className="z-caption text-muted-foreground">kcal</span>
        </div>
      </div>
    </div>
  );
};
